import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { calendarGetRange } from "../api";
import { mapDefaultEvent } from "../common/fullcalendar/Utils.js";

export const useCalendarEvents = ({
  queryKey,
  dateRange,
  enabled,
  requestOptions,
  holidayRequestOptions,
  transformEvent,
  t,
  language,
}) => {
  const fetchEvents = async ({ queryKey: qk }) => {
    const [, range] = qk;
    const start = range?.start;
    const end = range?.end;

    if (!start || !end) return [];

    const res = await calendarGetRange(start, end, requestOptions);
    if (res.status !== 200) throw new Error("Failed to load events");

    const baseEvents = res.data.map((row) => {
      const isHolidayEvent = !row.startDate && row.date;
      if (isHolidayEvent) {
        return {
          id: row.id,
          title: row.description,
          holidayDescKey: row.descKey,
          start: row.date,
          end: row.date,
          isHoliday: true,
        };
      }

      const mapped = transformEvent ? transformEvent(row) : mapDefaultEvent(row);
      return { ...mapped, isHoliday: false };
    });

    if (!holidayRequestOptions) {
      return baseEvents;
    }

    const holidayRes = await calendarGetRange(start, end, holidayRequestOptions);
    if (holidayRes.status !== 200) {
      return baseEvents;
    }

    const holidayEvents = holidayRes.data
      .filter((row) => !row.startDate && row.date)
      .map((row) => ({
        id: row.id,
        title: row.description,
        holidayDescKey: row.descKey,
        start: row.date,
        end: row.date,
        isHoliday: true,
      }));

    const existingHolidayKeySet = new Set(
      baseEvents.filter((e) => e.isHoliday).map((e) => `${e.id}-${e.start}`),
    );

    const missingHolidays = holidayEvents.filter((e) => !existingHolidayKeySet.has(`${e.id}-${e.start}`));

    return [...baseEvents, ...missingHolidays];
  };

  const {
    data: allEvents = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: [queryKey, dateRange],
    queryFn: fetchEvents,
    enabled,
  });

  const absenceEvents = useMemo(() => allEvents.filter((e) => !e.isHoliday), [allEvents]);

  const holidayDates = useMemo(
    () =>
      new Map(
        allEvents
          .filter((e) => e.isHoliday)
          .map((e) => [e.start, e.holidayDescKey ? t(e.holidayDescKey) : e.title]),
      ),
    [allEvents, t, language],
  );

  return {
    allEvents,
    absenceEvents,
    holidayDates,
    isLoading,
    error,
  };
}